import { useMemo } from "react";

import { useQuery } from "@apollo/client";
import { type SetterOrUpdater, useRecoilState } from "recoil";

import { type MetricField } from "../../gql-generated/types";
import { type ApolloResult } from "../../shared/models";
import { type GetTopLensesQuery, type GetTopLensesQueryVariables, getTopLenses } from "../api/queries";
import { generateFieldsList } from "../generators";
import { TopLensesOptions } from "../models";
import { topLensesAtom } from "../state";

import { useReportingGranularity } from "./useReportingGranularity";

export const useTopLensesStats = (): ApolloResult<{
    topLensesStats: GetTopLensesQuery | undefined;
    selectedTopLenses: MetricField;
    selectedTopLensesLabel: string;
    setSelectedTopLenses: SetterOrUpdater<MetricField>;
}> => {
    const [selectedTopLenses, setSelectedTopLenses] = useRecoilState(topLensesAtom);

    const { startTime, endTime, selectedGranularity } = useReportingGranularity();
    const {
        data: topLensesStats,
        error,
        loading,
    } = useQuery<GetTopLensesQuery, GetTopLensesQueryVariables>(getTopLenses, {
        variables: {
            granularity: selectedGranularity,
            startTime,
            endTime,
            sortBy: selectedTopLenses,
            fieldsList: generateFieldsList([selectedTopLenses]),
        },
    });

    const selectedTopLensesLabel = useMemo(
        () => TopLensesOptions.find(({ value }) => value === selectedTopLenses)?.label ?? "",
        [selectedTopLenses],
    );

    return {
        selectedTopLenses,
        selectedTopLensesLabel,
        topLensesStats,
        setSelectedTopLenses,
        error,
        loading,
    };
};
